import React, { createContext, useContext, useState, useEffect } from 'react';
import LoreService from '../../../services/LoreService';
import accountStore from '../../../components/account/AccountStore';

const LoreContext = createContext();

export const useLore = () => {
  const context = useContext(LoreContext);
  if (!context) {
    throw new Error('useLore must be used within a LoreProvider');
  }
  return context;
};

export const LoreProvider = ({ children }) => {
  const [loreData, setLoreData] = useState(null);
  const [playerProgress, setPlayerProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadLoreData = async () => {
      try {
        const data = await LoreService.getLoreData();
        setLoreData(data);
      } catch (err) {
        console.error('Error loading lore data:', err);
        setError('Failed to load lore data');
      }
    };

    const loadPlayerProgress = async () => {
      const profile = accountStore.getProfile();
      if (profile?.playerId) {
        try {
          const progress = await LoreService.getPlayerLoreProgress(profile.playerId);
          setPlayerProgress(progress || { completedLore: {}, unlockedTiers: ['tier_1'], totalCompleted: 0 });
        } catch (err) {
          console.error('Error loading player progress:', err);
          setError('Failed to load player progress');
        }
      }
    };

    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadLoreData(), loadPlayerProgress()]);
      setLoading(false);
    };

    loadData();
  }, []);

  const getAvailableLore = () => {
    if (!loreData || !playerProgress) return [];

    return Object.entries(loreData).reduce((acc, [tierId, tierData]) => {
      // Check if tier is unlocked
      if (!playerProgress.unlockedTiers.includes(tierId)) return acc;

      // Add all lore entries from this tier
      const loreEntries = Object.entries(tierData).map(([loreId, loreContent]) => ({
        id: loreId,
        tier: tierId,
        ...loreContent,
        completed: Boolean(playerProgress.completedLore[loreId])
      }));

      return [...acc, ...loreEntries];
    }, []);
  };

  const refreshProgress = React.useCallback(async () => {
    const profile = accountStore.getProfile();
    if (profile?.playerId) {
      try {
        const progress = await LoreService.getPlayerLoreProgress(profile.playerId);
        
        setPlayerProgress(progress || {
          tier_1: [],
          tier_2: [],
          tier_3: [],
          tier_4: [],
          totalCompleted: 0
        });
      } catch (error) {
        console.error('Error refreshing progress:', error);
      }
    }
  }, []);

  const value = {
    loreData,
    playerProgress,
    loading,
    error,
    getAvailableLore,
    refreshProgress
  };

  if (loading) {
    return <div>Loading lore data...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <LoreContext.Provider value={value}>
      {children}
    </LoreContext.Provider>
  );
};